import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Phone, Mail, MessageCircle, Calendar, Filter, X, Save,
  ArrowLeft, FileText, TrendingUp, Clock, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getLeads, updateLead, deleteLead } from '../services/api';
import { isAuthenticated } from '../services/api';

const LeadsManager = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin');
      return;
    }
    fetchLeads();
  }, [navigate]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await getLeads();
      setLeads(data);
      setFilteredLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterEstado) {
      setFilteredLeads(leads.filter(lead => lead.estado === filterEstado));
    } else {
      setFilteredLeads(leads);
    }
  }, [filterEstado, leads]);

  const handleUpdateEstado = async (leadId, newEstado) => {
    try {
      setUpdating(true);
      await updateLead(leadId, { estado: newEstado });
      await fetchLeads();
      if (selectedLead && selectedLead.id === leadId) {
        const updated = await getLeads();
        setSelectedLead(updated.find(l => l.id === leadId));
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async (leadId) => {
    if (!newNote.trim()) return;
    
    try {
      setUpdating(true);
      await updateLead(leadId, { nuevaNota: newNote });
      setNewNote('');
      await fetchLeads();
      if (selectedLead && selectedLead.id === leadId) {
        const updated = await getLeads();
        setSelectedLead(updated.find(l => l.id === leadId));
      }
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    
    try {
      await deleteLead(leadId);
      await fetchLeads();
      setSelectedLead(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      nuevo: 'bg-blue-100 text-blue-700',
      contactado: 'bg-purple-100 text-purple-700',
      interesado: 'bg-yellow-100 text-yellow-700',
      negociando: 'bg-orange-100 text-orange-700',
      cerrado: 'bg-green-100 text-green-700',
      perdido: 'bg-red-100 text-red-700'
    };
    return colors[estado] || 'bg-gray-100 text-gray-700';
  };

  const getEstadoIcon = (estado) => {
    const icons = {
      nuevo: <Clock size={16} />,
      contactado: <Phone size={16} />,
      interesado: <TrendingUp size={16} />,
      negociando: <MessageCircle size={16} />,
      cerrado: <CheckCircle size={16} />,
      perdido: <XCircle size={16} />
    };
    return icons[estado] || <Users size={16} />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const estadosDisponibles = [
    { value: 'nuevo', label: 'Nuevo' },
    { value: 'contactado', label: 'Contactado' },
    { value: 'interesado', label: 'Interesado' },
    { value: 'negociando', label: 'Negociando' },
    { value: 'cerrado', label: 'Cerrado' },
    { value: 'perdido', label: 'Perdido' }
  ];

  const stats = {
    total: leads.length,
    nuevo: leads.filter(l => l.estado === 'nuevo').length,
    interesado: leads.filter(l => l.estado === 'interesado').length,
    cerrado: leads.filter(l => l.estado === 'cerrado').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando leads...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-blue-200 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Volver al Dashboard
          </button>
          <div className="flex items-center gap-3">
            <Users size={32} />
            <div>
              <h1 className="text-2xl font-bold">Gestión de Leads</h1>
              <p className="text-blue-200 text-sm">Sistema de seguimiento de clientes potenciales</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Leads</p>
                <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
              </div>
              <Users className="text-blue-900" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Nuevos</p>
                <p className="text-3xl font-bold text-blue-600">{stats.nuevo}</p>
              </div>
              <Clock className="text-blue-600" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Interesados</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.interesado}</p>
              </div>
              <TrendingUp className="text-yellow-600" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Cerrados</p>
                <p className="text-3xl font-bold text-green-600">{stats.cerrado}</p>
              </div>
              <CheckCircle className="text-green-600" size={40} />
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-blue-900" />
              <span className="font-semibold text-gray-800">Filtrar por estado:</span>
            </div>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none"
            >
              <option value="">Todos</option>
              {estadosDisponibles.map(estado => (
                <option key={estado.value} value={estado.value}>{estado.label}</option>
              ))}
            </select>
            {filterEstado && (
              <Button
                onClick={() => setFilterEstado('')}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <X size={16} />
                Limpiar
              </Button>
            )}
          </div>
        </div>

        {/* Lista de leads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Columna izquierda - Lista */}
          <div className="space-y-4">
            {filteredLeads.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <Users className="text-gray-400 mx-auto mb-4" size={64} />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No hay leads</h3>
                <p className="text-gray-600">
                  {filterEstado ? 'No hay leads con este estado' : 'Aún no has recibido leads'}
                </p>
              </div>
            ) : (
              filteredLeads.map(lead => (
                <div
                  key={lead.id}
                  onClick={() => setSelectedLead(lead)}
                  className={`bg-white rounded-xl shadow-md p-6 cursor-pointer transition-all hover:shadow-lg ${
                    selectedLead?.id === lead.id ? 'ring-2 ring-blue-900' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{lead.nombre}</h3>
                      <p className="text-sm text-gray-600">{lead.email}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getEstadoColor(lead.estado)}`}>
                      {getEstadoIcon(lead.estado)}
                      {lead.estado}
                    </span>
                  </div>
                  
                  {lead.propiedadTitulo && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-3">
                      <p className="text-sm font-medium text-blue-900">
                        Interesado en: {lead.propiedadTitulo}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-gray-700 text-sm line-clamp-2 mb-3">{lead.mensaje}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Phone size={14} />
                      {lead.telefono}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(lead.fechaCreacion)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Columna derecha - Detalle */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            {selectedLead ? (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-blue-900">Detalle del Lead</h2>
                  <button
                    onClick={() => setSelectedLead(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Información del lead */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Nombre</label>
                    <p className="text-lg font-semibold text-gray-800">{selectedLead.nombre}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
                        <Mail size={14} /> Email
                      </label>
                      <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">
                        {selectedLead.email}
                      </a>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
                        <Phone size={14} /> Teléfono
                      </label>
                      <a href={`tel:${selectedLead.telefono}`} className="text-blue-600 hover:underline">
                        {selectedLead.telefono}
                      </a>
                    </div>
                  </div>

                  {selectedLead.propiedadTitulo && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Propiedad de Interés</label>
                      <p className="text-gray-800 font-medium">{selectedLead.propiedadTitulo}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-600">Mensaje</label>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedLead.mensaje}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">Estado</label>
                    <select
                      value={selectedLead.estado}
                      onChange={(e) => handleUpdateEstado(selectedLead.id, e.target.value)}
                      disabled={updating}
                      className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none"
                    >
                      {estadosDisponibles.map(estado => (
                        <option key={estado.value} value={estado.value}>{estado.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notas */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FileText size={20} />
                    Notas de Seguimiento
                  </h3>

                  {selectedLead.notas && selectedLead.notas.length > 0 ? (
                    <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                      {selectedLead.notas.map(nota => (
                        <div key={nota.id} className="bg-blue-50 rounded-lg p-3">
                          <p className="text-gray-700 text-sm mb-1">{nota.texto}</p>
                          <p className="text-xs text-gray-500">
                            {nota.autor} - {formatDate(nota.fecha)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm mb-4">No hay notas aún</p>
                  )}

                  <div className="flex gap-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Agregar nota de seguimiento..."
                      rows="3"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none resize-none"
                    />
                    <Button
                      onClick={() => handleAddNote(selectedLead.id)}
                      disabled={!newNote.trim() || updating}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                    >
                      <Save size={20} />
                    </Button>
                  </div>
                </div>

                {/* Acciones */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex gap-3">
                    <a
                      href={`https://wa.me/${selectedLead.telefono.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium text-center flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={20} />
                      WhatsApp
                    </a>
                    <button
                      onClick={() => handleDelete(selectedLead.id)}
                      className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <Users className="text-gray-400 mx-auto mb-4" size={64} />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Selecciona un lead</h3>
                <p className="text-gray-600">
                  Haz clic en un lead de la lista para ver sus detalles y realizar seguimiento
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadsManager;
